
import { Lead } from "@/types/lead";
import { toast } from "@/hooks/use-toast";
import { importPlaceAsLead } from "@/services/places/importService";
import { PlaceResult } from "@/types/place";
import { parseCSVToLeads } from "./parserService";
import { searchPlaces } from "@/services/places/searchService";

interface ImportResults {
  success: number;
  failed: number;
  errors: string[];
}

// Helper function to search for a place using lead data
async function findBestPlaceMatch(lead: Partial<Lead>): Promise<PlaceResult | null> {
  try {
    // Construct a search query using lead name and address
    const searchQuery = `${lead.name || ''} ${lead.address || ''}`.trim();
    
    if (!searchQuery) {
      console.error('Cannot search for place: No name or address provided in CSV data');
      return null;
    }
    
    console.log(`Searching for place: "${searchQuery}"`);
    
    // Use the existing searchPlaces function to find a match
    const response = await searchPlaces(searchQuery, '', 10000); // Use a large radius to ensure we get results
    
    if (response.status !== 'OK' || !response.results?.length) {
      console.error('No place found for:', searchQuery);
      return null;
    }
    
    // Get the best match (first result from Google Places)
    const bestMatch = response.results[0];
    console.log('Found place match:', bestMatch.name, 'with address:', bestMatch.formatted_address);
    
    return bestMatch;
  } catch (error) {
    console.error('Error searching for place match:', error);
    return null;
  }
}

export const importLeadsFromCSV = async (
  csvContent: string,
  onProgress?: (current: number, total: number) => void
): Promise<ImportResults> => {
  try {
    const leads = parseCSVToLeads(csvContent);
    
    if (!leads.length) {
      return {
        success: 0,
        failed: 0,
        errors: ["No valid leads found in CSV file"]
      };
    }
    
    let successCount = 0;
    const failedLeads: string[] = [];
    const total = leads.length;
    
    for (let i = 0; i < leads.length; i++) {
      const lead = leads[i];
      if (onProgress) {
        // Update progress to show searching for place
        onProgress(i, total);
      }
      
      try {
        // Step 1: Find best place match using Google Places search
        const placeMatch = await findBestPlaceMatch(lead);
        
        if (!placeMatch) {
          throw new Error(`No Google Places match found for "${lead.name || 'Unnamed lead'}"`);
        }
        
        // Step 2: Preserve any additional data from CSV that isn't in the Places API result
        const enhancedPlace: PlaceResult = {
          ...placeMatch,
          // Override any Place API data with CSV data if provided
          name: lead.name || placeMatch.name,
          international_phone_number: lead.phone || placeMatch.international_phone_number,
          website: lead.website || placeMatch.website,
        };
        
        if (lead.latitude && lead.longitude) {
          // If CSV includes coordinate data and it's valid, use it
          enhancedPlace.geometry = {
            location: {
              lat: typeof lead.latitude === 'number' ? lead.latitude : 
                   typeof lead.latitude === 'string' ? Number(lead.latitude) : placeMatch.geometry.location.lat,
              lng: typeof lead.longitude === 'number' ? lead.longitude : 
                   typeof lead.longitude === 'string' ? Number(lead.longitude) : placeMatch.geometry.location.lng
            }
          };
        }

        // Step 3: Import the enhanced place as a lead
        console.log('Importing place with place_id:', enhancedPlace.place_id);
        const importedLead = await importPlaceAsLead(enhancedPlace, lead.category);
        
        if (importedLead) {
          successCount++;
        } else {
          throw new Error('Lead import failed');
        }
      } catch (error) {
        console.error('Error importing lead:', error);
        failedLeads.push(`Failed to import lead "${lead.name || 'Unnamed'}": ${(error as Error).message || 'Unknown error'}`);
      }
    }
    
    return {
      success: successCount,
      failed: failedLeads.length,
      errors: failedLeads
    };
  } catch (error) {
    console.error('Error in importLeadsFromCSV:', error);
    throw error;
  }
};
